/**
 * MQTT 会话在线心跳：服务端 ping → 前端 pong；收到 stop 走统一 401 登出。
 * 全局只注册一次 message handler，MQTT 重连后自动恢复 downstream 订阅。
 */

import { mqttService } from './mqttService';
import { authService } from './authService';
import { logger } from '../utils/logger';

type SessionHeartbeatPayload = {
  action: 'ping' | 'pong' | 'stop';
  seq?: number;
  reason?: string;
  timestamp?: number;
};

class MqttSessionPresenceService {
  private userId: string | null = null;
  private downstreamTopic: string | null = null;
  private upstreamTopic: string | null = null;
  private messageUnsub: (() => void) | null = null;
  private subscribePromise: Promise<void> | null = null;

  activate(userId: string | number): void {
    const id = String(userId).trim();
    if (!id) return;

    if (this.userId === id && this.messageUnsub) {
      void this.ensureSubscribed();
      return;
    }

    this.deactivate();
    this.userId = id;
    this.downstreamTopic = `user/${id}/presence/heartbeat/downstream`;
    this.upstreamTopic = `user/${id}/presence/heartbeat/upstream`;
    this.ensureMessageHandler();
    void this.ensureSubscribed();
  }

  /** 登录 / App 启动后：连接 MQTT 并完成会话心跳订阅 */
  async ensureReady(): Promise<void> {
    if (!this.userId) return;
    if (sessionStorage.getItem('wavelink_logged_out') === 'true') return;
    if (!authService.isAuthenticated()) return;

    await mqttService.connect();
    await this.ensureSubscribed();
  }

  /** MQTT connect / resubscribeTopics 完成后调用 */
  onMqttConnected(): void {
    if (!this.userId || !this.downstreamTopic) return;
    void this.ensureSubscribed();
  }

  deactivate(): void {
    const topic = this.downstreamTopic;
    this.subscribePromise = null;

    if (this.messageUnsub) {
      this.messageUnsub();
      this.messageUnsub = null;
    }

    if (topic && mqttService.isServiceInitialized() && mqttService.isConnected()) {
      try {
        void mqttService.getMqttManager().unsubscribe(topic).catch(() => undefined);
      } catch {
        // mqtt 未初始化时忽略
      }
    }

    this.userId = null;
    this.downstreamTopic = null;
    this.upstreamTopic = null;
  }

  private ensureMessageHandler(): void {
    if (this.messageUnsub) return;

    const handler = (topic: string, raw: string | Buffer) => {
      if (!this.downstreamTopic || topic !== this.downstreamTopic) return;
      this.handleDownstream(raw);
    };

    if (!mqttService.isServiceInitialized()) {
      return;
    }

    try {
      this.messageUnsub = mqttService.getMqttManager().addMessageListener(handler);
    } catch (error) {
      logger.warn('注册会话心跳 message handler 失败:', error);
    }
  }

  private async ensureSubscribed(): Promise<void> {
    if (!this.downstreamTopic || !this.userId) return;
    if (sessionStorage.getItem('wavelink_logged_out') === 'true') return;
    if (!authService.isAuthenticated()) return;

    if (this.subscribePromise) {
      await this.subscribePromise;
      return;
    }

    this.subscribePromise = (async () => {
      try {
        if (!mqttService.isServiceInitialized()) {
          await mqttService.connect();
        } else if (!mqttService.isConnected()) {
          await mqttService.connect();
        }

        if (!this.messageUnsub) {
          this.ensureMessageHandler();
        }

        const mgr = mqttService.getMqttManager();
        await mgr.subscribe(this.downstreamTopic!, 1);
        logger.debug('会话 presence 心跳已订阅', { topic: this.downstreamTopic });
      } catch (error) {
        logger.warn('订阅会话 presence 心跳失败:', error);
        throw error;
      } finally {
        this.subscribePromise = null;
      }
    })();

    await this.subscribePromise;
  }

  private handleDownstream(raw: string | Buffer): void {
    let payload: SessionHeartbeatPayload;
    try {
      const text = typeof raw === 'string' ? raw : raw.toString();
      payload = JSON.parse(text) as SessionHeartbeatPayload;
    } catch (error) {
      logger.warn('解析会话心跳消息失败:', error);
      return;
    }

    if (payload.action === 'stop') {
      void authService.handleUnauthorized(payload.reason ?? '会话已结束');
      return;
    }

    if (payload.action !== 'ping' || !this.upstreamTopic) return;

    try {
      const mgr = mqttService.getMqttManager();
      void mgr.publishJson(
        this.upstreamTopic,
        {
          action: 'pong',
          seq: payload.seq,
          timestamp: Date.now(),
        },
        1,
        false,
      );
    } catch (error) {
      logger.warn('回复会话 presence pong 失败:', error);
    }
  }
}

export const mqttSessionPresenceService = new MqttSessionPresenceService();
